import {
	BusinessPartner,
	CreateOrderLines,
	Item,
	Order,
	UpdateOrderDto,
} from "@ang/shared-types";
import BaseController from "./BaseController";
import { container } from "../di/container";
import { OrdersService } from "../service/orders/ordersService";
import JSONModel from "sap/ui/model/json/JSONModel";
import Dialog from "sap/m/Dialog";
import { BusinessPartnerService } from "../service/businessPartners/businessPartnersService";
import { CreateOrderDto } from "../generated-client";
import { ItemsService } from "../service/items/itemsService";
import SelectDialog from "sap/m/SelectDialog";
import formatter from "../model/formatter";
import Button from "sap/m/Button";
import Event from "sap/ui/base/Event";
import { User } from "../user";
import DatePicker from "sap/m/DatePicker";
import { SearchField$LiveChangeEvent } from "sap/m/SearchField";
import ListBinding from "sap/ui/model/ListBinding";
import Input from "sap/m/Input";
import Table from "sap/m/Table";
import ComboBox from "sap/m/ComboBox";
import { OrderModel } from "../model/OrderModel";
import { OrderValidationService } from "../service/validation/OrderValidationService";
import { OrderFilterService } from "../service/filter/OrderFilterService";
import { Button$ClickEvent } from "sap/ui/webc/main/Button";
import ListItem from "sap/ui/core/ListItem";

export default class OrdersController extends BaseController {
	private readonly ordersService: OrdersService =
		container.resolve(OrdersService);
	private readonly businessPartnerService: BusinessPartnerService =
		container.resolve(BusinessPartnerService);
	private readonly itemsService: ItemsService = container.resolve(ItemsService);

	// Models and Services
	private readonly orderModel: OrderModel = new OrderModel();
	private readonly validationService: OrderValidationService =
		new OrderValidationService();
	private readonly filterService: OrderFilterService = new OrderFilterService();

	private ordersModel: JSONModel;
	private createOrderModel: JSONModel;
	private editOrderModel: JSONModel;
	private businessPartnersModel: JSONModel;
	private itemsModel: JSONModel;

	private addOrderDialog: Dialog;

	private editOrderDialog: Dialog;
	private addItemDialog: SelectDialog;
	private editItemDialog: SelectDialog;
	private valueHelpDialog: SelectDialog;
	public formatter = formatter;

	// Cache for items to avoid multiple API calls
	private allItems: Item[] = [];
	private allOrders: Order[] = [];
	private loadAllItemsPromise: Promise<void> | null = null;
	private isBusinessPartnerLoaded = false;
	private isItemsLoaded = false;
	private addItemsOpenRequestId = 0;
	private editItemsOpenRequestId = 0;
	readOrderDialog: Dialog;

	/*
	 * Methods called when starting page
	 */

	onInit(): void {
		this.getView().setBusy(true);
		void this.initializeModels(
			() => this.setOrderCreateModel(),
			[
				() => this.setOrdersModel(),
				() => this.setBusinessPartnersModel(),
				() => this.loadAllItems(),
			],
		).finally(() => {
			this.getView().setBusy(false);
		});
	}

	private async setOrdersModel(): Promise<void> {
		const orders = await this.executeWithErrorHandling(
			() => this.getUserOrders(),
			await this.getText("loadingOrders"),
			undefined, // No success message for background loading
			await this.getText("failedToLoadOrders"),
		);

		if (orders) {
			this.ordersModel = new JSONModel(orders);
			console.log("Orders Model Data:", this.ordersModel);
			this.getView().setModel(this.ordersModel, "ordersModel");
		} else {
			// Set empty model on error
			this.ordersModel = new JSONModel([]);
			this.getView().setModel(this.ordersModel, "ordersModel");
		}
	}

	async onSubmitEditOrder(): Promise<void> {
		const isReady = await this.ensureModelsReady(
			() => !!this.editOrderModel,
			"Edit order data is not loaded yet.",
		);
		if (!isReady) {
			return;
		}

		const editOrderDTO: Order = this.editOrderModel.getData() as Order;
		console.log("Edit Order DTO:", editOrderDTO);

		//Pase to UpdateOrderDto
		const updateOrderDto: UpdateOrderDto =
			this.orderModel.createUpdateOrderDto(editOrderDTO);
		console.log("Mapped Update Order DTO:", updateOrderDto);

		const success = await this.executeWithErrorHandling(
			() =>
				this.ordersService.updateOrder(editOrderDTO.docEntry, updateOrderDto),
			await this.getText("updatingOrder"),
			await this.getText("orderUpdatedSuccessfully"),
			await this.getText("failedToUpdateOrder"),
		);

		// Close dialog and refresh orders list on success
		if (success !== null) {
			this.editOrderDialog.close();
			await this.setOrdersModel(); // Refresh orders list to show updated data
		}
	}

	async onEditOrdersOpen(event: Event): Promise<void> {
		const isReady = await this.ensureModelsReady(
			() => !!this.ordersModel,
			"Orders are still loading. Please wait.",
		);
		if (!isReady) {
			return;
		}

		const oButton = event.getSource() as Button;
		const oContext = oButton.getBindingContext("ordersModel");
		const oOrderData = oContext.getObject() as Order;

		console.log("Order Data for editing:", oOrderData);

		if (oOrderData.confirmed) {
			this.showWarningMessage(await this.getText("cannotEditConfirmedOrder"));
			return;
		}

		const editData = { ...oOrderData }; //QUEST: Date from the oldorder or today or tomorow
		editData.docDueDate = new Date(oOrderData.docDueDate);

		this.editOrderModel = new JSONModel(editData);

		this.editOrderDialog ??= (await this.loadFragment({
			name: "ang.order_portal.fragments.orders.EditOrdersDialog",
		})) as Dialog;

		this.editOrderDialog.setModel(this.editOrderModel, "editOrderModel");

		this.editOrderDialog.open();
	}

	private async getUserOrders(): Promise<Order[]> {
		const response = await this.ordersService.getAllUserOrders();
		this.allOrders = response.data as unknown as Order[];
		console.log("Fetched Orders: ", response);
		console.log("All Orders Cache: ", this.allOrders);
		return response.data as unknown as Order[];
	}

	private async setBusinessPartnersModel(): Promise<void> {
		const data = await this.executeWithErrorHandling(
			() => this.getBusinessPartner(User.getInstance().getCardCode()),
			"Loading business partner...",
			undefined,
			"Failed to load business partner",
		);
		this.isBusinessPartnerLoaded = data !== null;
		console.log("Fetched Business Partner: ", data);

		this.businessPartnersModel = new JSONModel(data ?? null);
		this.getView().setModel(
			this.businessPartnersModel,
			"businessPartnersModel",
		);
	}

	private async getBusinessPartner(cardCode: string): Promise<BusinessPartner> {
		const response =
			await this.businessPartnerService.getBusinessPartner(cardCode);
		return response.data[0] as unknown as BusinessPartner;
	}

	private async getItems(): Promise<Item[]> {
		const response = await this.itemsService.getItems();
		console.log("Fetched Items: ", response);
		return response.data as unknown as Item[];
	}

	private async loadAllItems(): Promise<void> {
		if (this.allItems.length > 0) {
			this.isItemsLoaded = true;
			return;
		}

		const hasPendingLoad = this.loadAllItemsPromise !== null;
		if (hasPendingLoad) {
			await this.loadAllItemsPromise;
			return;
		}

		this.loadAllItemsPromise = (async () => {
			const items = await this.executeWithErrorHandling(
				() => this.getItems(),
				await this.getText("loadingItems"),
				undefined,
				await this.getText("failedToLoadItems"),
			);

			if (items) {
				this.allItems = items;
				this.isItemsLoaded = true;
			} else {
				this.isItemsLoaded = false;
			}
		})();

		try {
			await this.loadAllItemsPromise;
		} finally {
			this.loadAllItemsPromise = null;
		}
	}

	private setItemsModelWithFilter(filterFn?: (item: Item) => boolean): void {
		const filteredItems = filterFn
			? this.allItems.filter(filterFn)
			: this.allItems;
		this.itemsModel = new JSONModel(filteredItems);
		this.getView().setModel(this.itemsModel, "itemsModel");
	}

	async onAddOrderDialogPress(): Promise<void> {
		const isReady = await this.ensureModelsReady(
			() =>
				!!this.createOrderModel &&
				this.isBusinessPartnerLoaded &&
				this.validationService.isValidCardCode(
					this.createOrderModel.getProperty("/cardCode") as string,
				),
			"Order data is still loading. Please wait and try again.",
		);
		if (!isReady) {
			return;
		}

		this.addOrderDialog ??= (await this.loadFragment({
			name: "ang.order_portal.fragments.orders.CreateOrdersDialog",
		})) as Dialog;

		this.addOrderDialog
			.setModel(this.createOrderModel, "createOrderModel")
			.setModel(this.businessPartnersModel, "businessPartnersModel");

		this.addOrderDialog.open();
	}

	onOrderSearch(event: SearchField$LiveChangeEvent | Event): void {
		// Handle different event types
		let query: string | undefined;

		// Check if this is a SearchField liveChange event by checking event ID
		if (
			"getParameter" in event &&
			event.getId &&
			event.getId() === "liveChange"
		) {
			// SearchField liveChange event
			query = (event as SearchField$LiveChangeEvent).getParameter("newValue");
		} else {
			// ComboBox or DatePicker change event - get current search value
			const searchField = this.byId("OrderSearch") as any;
			query = searchField?.getValue() || "";
		}

		const oTable = this.byId("orderTable") as Table;
		const oBinding = oTable.getBinding("items") as ListBinding;

		// Get filter values from UI
		const confirmedCombo = this.byId("OrderStatusCombo") as ComboBox;
		const statusKey = confirmedCombo?.getSelectedKey();

		const postingDateInput = this.byId("filterPostingDate") as DatePicker;
		const deliveryDateInput = this.byId("filterDeliveryDate") as DatePicker;

		const postingDate = postingDateInput?.getDateValue();
		const deliveryDate = deliveryDateInput?.getDateValue();

		// Use filter service to create filters
		const filters = this.filterService.createOrderFilters(
			query,
			statusKey,
			postingDate,
			deliveryDate,
		);

		// Apply filters
		oBinding.filter(filters);
	}

	async onClearFilters(event: Event<any, Input>): Promise<void> {
		// Clear all filter input fields
		const orderSearch = this.byId("OrderSearch") as Input | undefined;
		const orderStatusCombo = this.byId("OrderStatusCombo") as
			| ComboBox
			| undefined;
		const postingDate = this.byId("filterPostingDate") as
			| DatePicker
			| undefined;
		const deliveryDate = this.byId("filterDeliveryDate") as
			| DatePicker
			| undefined;

		orderSearch?.setValue("");
		orderStatusCombo?.setSelectedKey("");
		postingDate?.setDateValue(null);
		deliveryDate?.setDateValue(null);

		// Remove all filters from the table binding
		const oTable = this.byId("orderTable") as Table;
		const oBinding = oTable.getBinding("items") as ListBinding;
		oBinding.filter([]);

		// Refresh the model data to show all orders (fetch latest from backend)
		this.setOrdersModel();

		// Optionally, reset value states for filter controls
		postingDate?.setValueState("None");
		deliveryDate?.setValueState("None");
	}

	onItemSearch(oEvent: Event<any, Input>): void {
		const sValue = oEvent.getParameter("value") as string;
		const oBinding = oEvent.getParameter("itemsBinding");

		// Use filter service to create item name filter
		const filters = this.filterService.createItemNameFilter(sValue);
		oBinding.filter(filters);
	}

	async onSubmitEditItems(event: any): Promise<void> {
		const isReady = await this.ensureModelsReady(
			() => !!this.editOrderModel,
			"Edit order data is not loaded yet.",
		);
		if (!isReady) {
			return;
		}

		const aSelectedItems = event.getParameter("selectedItems");

		const aExistingLines = this.editOrderModel.getProperty(
			"/documentLines",
		) as CreateOrderLines[];

		console.log("Selected Items from dialog:", aSelectedItems);
		console.log("Existing Document Lines:", aExistingLines);

		// Delegate document line creation to the model
		const aNewDocumentLines = this.orderModel.createDocumentLinesFromItems(
			aSelectedItems,
			aExistingLines,
		);
		this.editOrderModel.setProperty("/documentLines", aNewDocumentLines);
		this.updateEditButton();
		this.calculateAndSetEditDocTotal();
	}

	async onSubmitItems(event: any): Promise<void> {
		const isReady = await this.ensureModelsReady(
			() => !!this.createOrderModel,
			"Create order data is not loaded yet.",
		);
		if (!isReady) {
			return;
		}

		const aSelectedItems = event.getParameter("selectedItems");

		const aExistingLines = this.createOrderModel.getProperty(
			"/documentLines",
		) as CreateOrderLines[];

		console.log("Selected Items from dialog:", aSelectedItems);
		console.log("Existing Document Lines:", aExistingLines);

		// Delegate document line creation to the model
		// QUEST: Wie gestalte ich das so das nur manuell gelöschte Elemente aus der liste genommen werden?
		const aNewDocumentLines = this.orderModel.createDocumentLinesFromItems(
			aSelectedItems,
			aExistingLines,
		);

		this.createOrderModel.setProperty("/documentLines", aNewDocumentLines);
		this.updateButton();
		this.calculateAndSetCreateDocTotal();
	}

	onCloseOrdersDialog(): void {
		(this.byId("CreateOrderDialog") as Dialog)?.close();
		this.cancelAddItemsOpenRequests();
		this.setOrderCreateModel(); // Reset the create order model
		this.updateButton(); // Disable submit button after closing
	}

	onCloseEditDialog(): void {
		(this.byId("EditOrderDialog") as Dialog)?.close();
		this.cancelEditItemsOpenRequests();
		this.editOrderModel = null;
		this.updateEditButton(); // Disable submit button after closing
	}

	private setOrderCreateModel(): void {
		const orderModel = this.orderModel.createDefaultOrderDto(
			User.getInstance().getUsername() ?? "unknown",
			User.getInstance().getCardCode() ?? "unknown",
		);
		this.createOrderModel = new JSONModel(orderModel);
	}

	async onSubmitCreateOrder(): Promise<void> {
		const isReady = await this.ensureModelsReady(
			() =>
				!!this.createOrderModel &&
				this.isBusinessPartnerLoaded &&
				this.validationService.isValidCardCode(
					this.createOrderModel.getProperty("/cardCode") as string,
				),
			"Order data is still loading. Please wait before submitting.",
		);
		if (!isReady) {
			return;
		}

		const createOrderDTO: CreateOrderDto =
			this.createOrderModel.getData() as CreateOrderDto;

		// Validate order data using validation service
		const validationError =
			this.validationService.validateOrderData(createOrderDTO);
		if (validationError) {
			this.showWarningMessage(validationError);
			return;
		}

		console.log("Create Order DTO:", createOrderDTO);

		const success = await this.executeWithErrorHandling(
			() => this.createOrder(createOrderDTO),
			await this.getText("creatingOrder"),
			await this.getText("orderCreatedSuccessfully"),
			await this.getText("failedToCreateOrder"),
		);

		if (success !== null) {
			this.onCloseOrdersDialog();
			await this.setOrdersModel(); // Refresh orders list
		}
	}

	private async createOrder(createOrderDTO: CreateOrderDto): Promise<void> {
		await this.ordersService.createOrder(createOrderDTO);
	}

	/*
	 * Item Dialog
	 */
	async onShowAddItemDialog(requestId?: number): Promise<void> {
		if (
			typeof requestId === "number" &&
			!this.isAddItemsOpenRequestValid(requestId)
		) {
			return;
		}

		const isReady = await this.ensureModelsReady(
			() => this.isItemsLoaded && !!this.itemsModel,
			"Items are still loading. Please wait and try again.",
		);
		if (!isReady) {
			return;
		}

		if (
			typeof requestId === "number" &&
			!this.isAddItemsOpenRequestValid(requestId)
		) {
			return;
		}

		this.addItemDialog ??= (await this.loadFragment({
			name: "ang.order_portal.fragments.orders.AddItems",
		})) as SelectDialog;

		// Set the items model on the dialog
		this.addItemDialog.setModel(this.itemsModel, "itemsModel");
		this.addItemDialog.open("");

		// Pre-select items that are already in the order after dialog is open
		this.preselectExistingItems();
	}

	/**
	 * TODO: In Model
	 * General delete function for removing items from document lines
	 * @param oEvent Button click event from delete button
	 * @param modelName Name of the model to use
	 * @param shouldUpdateButton Whether to update button state after deletion
	 */
	private deleteItemFromDocumentLines(
		oEvent: Button$ClickEvent,
		modelName: string,
		shouldUpdateButton: boolean = false,
	): void {
		const oButton = oEvent.getSource();
		const oContext = oButton.getBindingContext(modelName);

		if (oContext) {
			const sPath = oContext.getPath();
			const aPathParts = sPath.split("/");
			const iIndex = parseInt(aPathParts[aPathParts.length - 1]);

			// Get the appropriate model
			const model =
				modelName === "createOrderModel"
					? this.createOrderModel
					: this.editOrderModel;
			const aDocumentLines = model.getProperty(
				"/documentLines",
			) as CreateOrderLines[];

			// Delegate to model for data manipulation
			const updatedLines = this.orderModel.removeDocumentLine(
				aDocumentLines,
				iIndex,
			);
			model.setProperty("/documentLines", updatedLines);

			// Update button state if needed
			if (shouldUpdateButton) {
				if (modelName === "createOrderModel") {
					this.updateButton();
				} else {
					this.updateEditButton();
				}
			}

			// Recalculate total after deletion
			this.calculateAndSetDocTotal(model);
		}
	}

	/**
	 * Deletes an item from the create order document lines
	 * @param oEvent Button click event from delete button
	 */
	public onDeleteItemArray(oEvent: Button$ClickEvent): void {
		this.deleteItemFromDocumentLines(oEvent, "createOrderModel", true);
	}

	/**
	 * Deletes an item from the edit order document lines
	 * @param oEvent Button click event from delete button
	 */
	public onDeleteEditItemArray(oEvent: Button$ClickEvent): void {
		this.deleteItemFromDocumentLines(oEvent, "editOrderModel", true);
	}

	private validateDeliveryDate(oEvent: any, modelType?: string): void {
		const oDatePicker = oEvent.getSource() as DatePicker;
		const oSelectedDate = oDatePicker.getDateValue();

		// Use validation service for date validation
		const validationResult =
			this.validationService.validateDeliveryDate(oSelectedDate);

		oDatePicker.setValueState(validationResult.valueState);
		if (validationResult.errorMessage) {
			oDatePicker.setValueStateText(validationResult.errorMessage);
		} else {
			// Determine which model to update
			if (modelType === "edit") {
				this.editOrderModel.setProperty("/docDueDate", oSelectedDate);
			} else {
				this.createOrderModel.setProperty("/docDueDate", oSelectedDate);
			}
		}
	}

	validateCreateDeliveryDate(oEvent: any): void {
		this.validateDeliveryDate(oEvent, "create");
		this.updateButton();
	}

	validateEditDeliveryDate(oEvent: any): void {
		this.validateDeliveryDate(oEvent, "edit");
		this.updateEditButton();
	}

	updateButton() {
		const oButton = this.byId("submitCreateOrderButton") as Button;

		// Check if create order model exists before trying to access it
		if (!this.createOrderModel) {
			console.log("Create order model is null, disabling button");
			oButton.setEnabled(false);
			return;
		}

		//Line validation
		const aDocumentLines = this.createOrderModel.getProperty("/documentLines");
		let listNotEmpty =
			this.validationService.validateDocumentLinesExist(aDocumentLines);

		// Card code validation
		listNotEmpty &&= this.validationService.isValidCardCode(
			this.createOrderModel.getProperty("/cardCode") as string,
		);

		//Date validation
		listNotEmpty &&= this.validationService.validateDeliveryDate(
			this.createOrderModel.getProperty("/docDueDate"),
		).isValid;

		oButton.setEnabled(listNotEmpty);
	}

	/**
	 * Updates the edit order submit button state based on validation
	 */
	updateEditButton(): void {
		const oButton = this.byId("submitEditOrderButton") as Button;

		// Check if edit order model exists before trying to access it
		if (!this.editOrderModel) {
			console.log("Edit order model is null, disabling button");
			oButton.setEnabled(false);
			return;
		}

		//Line validation
		const aDocumentLines = this.editOrderModel.getProperty("/documentLines");
		let listNotEmpty =
			this.validationService.validateDocumentLinesExist(aDocumentLines);
		console.log("Edit Button Validation Result - Line:", listNotEmpty);
		//Date validation
		listNotEmpty &&= this.validationService.validateDeliveryDate(
			this.editOrderModel.getProperty("/docDueDate"),
		).isValid;
		console.log("Edit Button Validation Result - Date:", listNotEmpty);

		oButton.setEnabled(listNotEmpty);
	}

	/**
	 * Calculates and sets the document total for the specified model
	 * @param model The JSON model to calculate total
	 */
	private calculateAndSetDocTotal(model: JSONModel): void {
		if (!model || typeof model.getProperty !== "function") {
			console.warn("Invalid model passed to calculateAndSetDocTotal");
			return;
		}

		const aDocumentLines = model.getProperty(
			"/documentLines",
		) as CreateOrderLines[];
		console.log("Document Lines for total calculation:", aDocumentLines);

		if (!aDocumentLines || !Array.isArray(aDocumentLines)) {
			console.warn("No valid document lines found for total calculation");
			model.setProperty("/docTotal", "0.00");
			return;
		}

		let total = 0;

		aDocumentLines.forEach((line, index) => {
			const quantity = parseInt(String(line.quantity)) || 0;
			// Access price directly from the line since it should already contain the price from Item
			const price = line.price || 0;

			const lineTotal = price * quantity;

			console.log(
				`Line ${index}: quantity=${quantity}, price=${price}, lineTotal=${lineTotal}`,
			);
			total += lineTotal;
		});

		console.log("Calculated total from line calculations:", total);
		model.setProperty("/docTotal", total.toFixed(2));
	}

	public calculateAndSetEditDocTotal(): void {
		if (this.editOrderModel) {
			this.calculateAndSetDocTotal(this.editOrderModel);
		}
	}

	public calculateAndSetCreateDocTotal(): void {
		if (this.createOrderModel) {
			this.calculateAndSetDocTotal(this.createOrderModel);
		}
	}

	private preselectExistingItems(): void {
		if (!this.addItemDialog || !this.itemsModel || !this.createOrderModel) {
			return;
		}

		const aDocumentLines = this.createOrderModel.getProperty(
			"/documentLines",
		) as CreateOrderLines[];

		if (!aDocumentLines || aDocumentLines.length === 0) {
			return;
		}

		// Use order model to get existing item codes
		const aExistingItemCodes =
			this.orderModel.getExistingItemCodes(aDocumentLines);

		//QUEST: Do i need that??
		setTimeout(() => {
			const aItems = this.addItemDialog.getItems();

			aItems.forEach((oItem) => {
				const oContext = oItem.getBindingContext("itemsModel");
				if (oContext) {
					const oItemData = oContext.getObject() as Item;
					if (aExistingItemCodes.includes(oItemData.itemCode)) {
						oItem.setSelected(true);
					}
				}
			});
		}, 100);
	}

	async onAddItemDialog(): Promise<void> {
		const requestId = this.startAddItemsOpenRequest();
		await this.loadAllItems();
		if (!this.isAddItemsOpenRequestValid(requestId)) {
			return;
		}
		if (!this.isItemsLoaded) {
			this.showWarningMessage("Items are still loading. Please try again.");
			return;
		}
		this.setItemsModelWithFilter();
		if (!this.isAddItemsOpenRequestValid(requestId)) {
			return;
		}
		await this.onShowAddItemDialog(requestId);
	}

	async onFastAddItemPositionDialog(): Promise<void> {
		const requestId = this.startAddItemsOpenRequest();
		await this.loadAllItems();
		if (!this.isAddItemsOpenRequestValid(requestId)) {
			return;
		}
		if (!this.isItemsLoaded) {
			this.showWarningMessage("Items are still loading. Please try again.");
			return;
		}
		this.setItemsModelWithFilter((item) => item.properties10);
		if (!this.isAddItemsOpenRequestValid(requestId)) {
			return;
		}
		await this.onShowAddItemDialog(requestId);
	}

	async onFastAddItemPositionSecondDialog(): Promise<void> {
		const requestId = this.startAddItemsOpenRequest();
		await this.loadAllItems();
		if (!this.isAddItemsOpenRequestValid(requestId)) {
			return;
		}
		if (!this.isItemsLoaded) {
			this.showWarningMessage("Items are still loading. Please try again.");
			return;
		}
		this.setItemsModelWithFilter((item) => item.properties11);
		if (!this.isAddItemsOpenRequestValid(requestId)) {
			return;
		}
		await this.onShowAddItemDialog(requestId);
	}

	async onEditItemDialog(): Promise<void> {
		const requestId = this.startEditItemsOpenRequest();
		await this.loadAllItems();
		if (!this.isEditItemsOpenRequestValid(requestId)) {
			return;
		}
		if (!this.isItemsLoaded) {
			this.showWarningMessage("Items are still loading. Please try again.");
			return;
		}
		this.setItemsModelWithFilter();
		if (!this.isEditItemsOpenRequestValid(requestId)) {
			return;
		}
		await this.onShowEditItemDialog(requestId);
	}

	async onFastEditItemPositionDialog(): Promise<void> {
		const requestId = this.startEditItemsOpenRequest();
		await this.loadAllItems();
		if (!this.isEditItemsOpenRequestValid(requestId)) {
			return;
		}
		if (!this.isItemsLoaded) {
			this.showWarningMessage("Items are still loading. Please try again.");
			return;
		}
		this.setItemsModelWithFilter((item) => item.properties10);
		if (!this.isEditItemsOpenRequestValid(requestId)) {
			return;
		}
		await this.onShowEditItemDialog(requestId);
	}

	async onFastEditItemPositionSecondDialog(): Promise<void> {
		const requestId = this.startEditItemsOpenRequest();
		await this.loadAllItems();
		if (!this.isEditItemsOpenRequestValid(requestId)) {
			return;
		}
		if (!this.isItemsLoaded) {
			this.showWarningMessage("Items are still loading. Please try again.");
			return;
		}
		this.setItemsModelWithFilter((item) => item.properties11);
		if (!this.isEditItemsOpenRequestValid(requestId)) {
			return;
		}
		await this.onShowEditItemDialog(requestId);
	}

	async onShowEditItemDialog(requestId?: number) {
		if (
			typeof requestId === "number" &&
			!this.isEditItemsOpenRequestValid(requestId)
		) {
			return;
		}

		const isReady = await this.ensureModelsReady(
			() => this.isItemsLoaded && !!this.itemsModel && !!this.editOrderModel,
			"Edit item data is not loaded yet.",
		);
		if (!isReady) {
			return;
		}

		if (
			typeof requestId === "number" &&
			!this.isEditItemsOpenRequestValid(requestId)
		) {
			return;
		}

		this.editItemDialog ??= (await this.loadFragment({
			name: "ang.order_portal.fragments.orders.EditItems",
		})) as SelectDialog;

		// Set the items model on the dialog
		this.editItemDialog.setModel(this.itemsModel, "itemsModel");
		this.editItemDialog.open("");

		this.calculateAndSetEditDocTotal();

		// Pre-select items that are already in the order after dialog is open
		this.preselectExistingItems();
	}

	async onDuplicateOrder(event: Event): Promise<void> {
		await this.waitForInitializedModels();
		if (!this.ordersModel) {
			this.showWarningMessage("Orders are still loading. Please wait.");
			return;
		}

		const oButton = event.getSource() as Button;
		const oContext = oButton.getBindingContext("ordersModel");
		const oOrderData = oContext.getObject() as Order;

		console.log("Order Data for duplication:", oOrderData);
		const duplicatedOrder = this.orderModel.duplicateOrder(oOrderData);
		this.createOrderModel = new JSONModel(duplicatedOrder);

		this.addOrderDialog ??= (await this.loadFragment({
			name: "ang.order_portal.fragments.orders.CreateOrdersDialog",
		})) as Dialog;
		this.addOrderDialog.setModel(this.createOrderModel, "createOrderModel");

		this.addOrderDialog.open();
		this.updateButton();
		this.calculateAndSetCreateDocTotal();
	}

	async onReadOrderDialogOpen(event: any): Promise<void> {
		await this.waitForInitializedModels();
		if (!this.ordersModel) {
			this.showWarningMessage("Orders are still loading. Please wait.");
			return;
		}

		console.log("Read Order Dialog Open Event:", event);
		const oListItem = event.getParameter("listItem");
		const oContext = oListItem.getBindingContext("ordersModel");
		const oOrderData = oContext.getObject() as Order;

		console.log("Order Data for reading:", oOrderData);
		this.readOrderDialog ??= (await this.loadFragment({
			name: "ang.order_portal.fragments.orders.ReadOrderDialog",
		})) as Dialog;
		this.readOrderDialog.setModel(new JSONModel(oOrderData), "orderModel");

		this.readOrderDialog.open();
	}

	onCloseOrderReadDialog(): void {
		this.readOrderDialog?.close();
	}
	/**
	 * Opens the value help dialog for item selection
	 * This is triggered when the user clicks the value help icon on the item input field
	 */
	async onValueHelpItemRequest(oEvent: Event): Promise<void> {
		await this.waitForInitializedModels();
		await this.loadAllItems();
		if (!this.isItemsLoaded) {
			this.showWarningMessage("Items are still loading. Please wait.");
			return;
		}

		// Create the value help dialog if it doesn't exist
		this.valueHelpDialog ??= (await this.loadFragment({
			name: "ang.order_portal.fragments.orders.AdditemsValueHelp",
		})) as SelectDialog;

		// Set the items model on the dialog with proper binding path
		this.valueHelpDialog.setModel(new JSONModel(this.allItems));

		// Store the source input field for later reference
		const oSource = oEvent.getSource();
		this.valueHelpDialog.data("sourceInput", oSource);

		this.valueHelpDialog.open("");
	}

	/**
	 * Handles the selection or cancellation from the value help dialog
	 * @param oEvent Event containing selected items or cancel action
	 */
	onAddItemsValueHelp(oEvent: any): void {
		if (!this.createOrderModel && !this.editOrderModel) {
			this.showWarningMessage("Order model is not loaded yet.");
			return;
		}

		console.log("Value Help Dialog Event:", oEvent);
		const oSelectedItem = oEvent.getParameter("selectedItem");
		const oDialog = oEvent.getSource() as SelectDialog;
		const oSourceInput = oDialog.data("sourceInput") as Input;

		if (!oSelectedItem || !oSourceInput) {
			return;
		}

		this.updateRowWithSelectedItem(oSelectedItem, oSourceInput);
	}

	/**
	 * Updates the row data with the selected item information
	 * @param oSelectedItem The selected item from the value help dialog
	 * @param oSourceInput The source input field
	 */
	private updateRowWithSelectedItem(
		oSelectedItem: any,
		oSourceInput: Input,
	): void {
		// Get the selected item data
		const oContext = oSelectedItem.getBindingContext();
		const oItemData = oContext.getObject() as Item;

		// Set the item code in the source input field
		oSourceInput.setValue(oItemData.itemCode);

		// Get the binding context of the row to update other fields
		const oRowContext =
			oSourceInput.getBindingContext("createOrderModel") ||
			oSourceInput.getBindingContext("editOrderModel");

		console.log("Row Context for updating fields:", oRowContext);
		if (!oRowContext) {
			return;
		}

		const sModelName = oSourceInput.getBindingContext("createOrderModel")
			? "createOrderModel"
			: "editOrderModel";

		const oModel = this.getModelByName(sModelName);
		if (!oModel) {
			return;
		}

		this.updateModelProperties(oModel, oRowContext.getPath(), oItemData);
		this.triggerCalculationUpdate(sModelName);
	}

	/**
	 * Gets the model by name
	 * @param sModelName The name of the model
	 * @returns The JSON model or null
	 */
	private getModelByName(sModelName: string): JSONModel | null {
		if (sModelName === "createOrderModel") {
			return this.createOrderModel;
		} else if (sModelName === "editOrderModel") {
			return this.editOrderModel;
		}
		return null;
	}

	/**
	 * Updates model properties with item data
	 * @param oModel The model to update
	 * @param sPath The path in the model
	 * @param oItemData The item data
	 */
	private updateModelProperties(
		oModel: JSONModel,
		sPath: string,
		oItemData: Item,
	): void {
		console.log("Updating model at path:", sPath, "with item data:", oItemData);
		oModel.setProperty(sPath + "/itemCode", oItemData.itemCode);
		oModel.setProperty(sPath + "/itemName", oItemData.itemName);
		oModel.setProperty(sPath + "/price", oItemData.price);
	}

	/**
	 * Triggers calculation update based on model name
	 * @param sModelName The name of the model
	 */
	private triggerCalculationUpdate(sModelName: string): void {
		if (sModelName === "createOrderModel") {
			this.calculateAndSetCreateDocTotal();
		} else {
			this.calculateAndSetEditDocTotal();
		}
	}

	private startAddItemsOpenRequest(): number {
		return ++this.addItemsOpenRequestId;
	}

	private cancelAddItemsOpenRequests(): void {
		this.addItemsOpenRequestId++;
	}

	private isAddItemsOpenRequestValid(requestId: number): boolean {
		return (
			requestId === this.addItemsOpenRequestId && this.isCreateDialogOpen()
		);
	}

	private startEditItemsOpenRequest(): number {
		return ++this.editItemsOpenRequestId;
	}

	private cancelEditItemsOpenRequests(): void {
		this.editItemsOpenRequestId++;
	}

	private isEditItemsOpenRequestValid(requestId: number): boolean {
		return requestId === this.editItemsOpenRequestId && this.isEditDialogOpen();
	}

	private isCreateDialogOpen(): boolean {
		const oDialog =
			this.addOrderDialog ||
			((this.byId("CreateOrderDialog") as Dialog) ?? null);
		return !!oDialog && oDialog.isOpen();
	}

	private isEditDialogOpen(): boolean {
		const oDialog =
			this.editOrderDialog ||
			((this.byId("EditOrderDialog") as Dialog) ?? null);
		return !!oDialog && oDialog.isOpen();
	}
}
